import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { SUCCESS_MESSAGES } from 'request-responses';
import { Lead } from 'models/Lead';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { getUserFullName } from 'utils';

import './WidthrawLeadModal.styles.scss';

type WidthrawLeadModalProps = {
  className?: string;
  lead?: Lead;
  onWithdraw: (revokeDataRoomAccess: boolean) => Promise<boolean>;
};

const WidthrawLeadModal: ForwardRefRenderFunction<
  OverlayRef,
  WidthrawLeadModalProps
> = (props, ref) => {
  const { className, lead, onWithdraw } = props;

  const [modal, modalRef] = useCallbackRef<OverlayRef>();
  const [isLoading, setIsLoading] = useState(false);

  useImperativeHandle(ref, () => modal, [modal]);

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);
  const { t } = useTranslation();

  const classes = classNames('widthraw-lead-modal', className);
  const { firstName, lastName, email } = lead || {};

  const leadName =
    firstName && lastName ? getUserFullName(firstName, lastName) : email;

  const hanldeWithdraw = useCallback(
    async (values: { revokeDataRoomAccess: boolean }) => {
      const { revokeDataRoomAccess } = values;

      try {
        setIsLoading(true);

        const isWithdrawn = await onWithdraw(revokeDataRoomAccess);

        if (isWithdrawn) popSuccess(t(SUCCESS_MESSAGES.WITHDRAW_LEAD));
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
        modal?.close();
      }
    },
    [modal, onWithdraw, popSuccess, popServerError, t],
  );

  return (
    <Modal ref={modalRef} className={classes}>
      <Form
        initialValues={{ revokeDataRoomAccess: false }}
        onSubmit={hanldeWithdraw}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <p>{t('withdrawInterestLeadTitle', { lead: leadName })}</p>

            <Field
              name="revokeDataRoomAccess"
              type="checkbox"
              label={t('alsoRevokeDataRoomAndFileAccess')}
              component={CheckboxField}
            />

            <div className="actions">
              <Button
                variant="link"
                disabled={isLoading}
                onClick={modal?.close}
              >
                {t('cancel')}
              </Button>

              <Button type="submit" disabled={isLoading}>
                {t('yesWithdrawInterest')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(WidthrawLeadModal);
